import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton,
    Container
} from '@mui/material';

/* ✅ ProDev Assets & Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* ✅ Styled Components - ProDev Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

const LiveDemoBox = styled(Box)(({ theme }) => ({
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px dashed goldenrod",
    marginTop: "15px"
}));

/**
 * APIDemo Component
 * Specialized ProDevConsult Module
 * Author: Hazzan
 */
export default function APIDemo() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ State Management */
    const [data, setData] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ Lifecycle: Async Data Fetching */
    useEffect(() => {
        const fetchAPIData = async () => {
            try {
                const response = await fetch("https://jsonplaceholder.typicode.com/users");
                const result = await response.json();
                setData(result.slice(0, 4));
            } catch (error) {
                console.error("ProDev System Error: API Fetch Failed", error);
            }
        };
        fetchAPIData();

        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) { setIsSpeaking(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const apiModules = useMemo(() => [
        {
            title: "1. Static JSON Simulation",
            tag: "Local Mocking",
            desc: "Before connecting to a live server, we often mock data locally. This allows Hazzan to build the UI structure independently of backend availability.",
            code: `const localData = [\n  { id: 1, name: "Hazzan", role: "Architect" },\n  { id: 2, name: "Alice", role: "Designer" }\n];\n\nlocalData.map(user => <UserCard key={user.id} data={user} />);`
        },
        {
            title: "2. Async/Await useEffect Pattern",
            tag: "REST Integration",
            desc: "The ProDev standard for fetching data uses an async function inside a useEffect hook. This ensures the UI remains responsive while data is in transit.",
            code: `useEffect(() => {\n  async function fetchAPIData() {\n    let res = await fetch("https://api.prodev.com/users");\n    let data = await res.json();\n    setData(data);\n  }\n  fetchAPIData();\n}, []);`
        },
        {
            title: "3. Interactive Selection",
            tag: "State Referencing",
            desc: "By storing the result of an API call in state, we can allow users to click specific nodes to drill down into detailed information.",
            code: `const [selected, setSelected] = useState(null);\n\n<Card onClick={() => setSelected(user)}>\n  <p>{selected ? selected.email : "Select a node"}</p>\n</Card>`
        },
        {
            title: "4. Error Boundary Logic",
            tag: "Resilient Systems",
            desc: "At ProDevConsult, we never assume an API will succeed. Wrap your fetch calls in try/catch blocks to handle network failures gracefully.",
            code: `try {\n  let data = await fetchUsers();\n  setApiData(data);\n} catch (error) {\n  console.error("ProDev Alert: Fetch failed", error);\n}`
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{
                    fontWeight: 800, backgroundColor: "gold", color: "#100720",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    display: "inline-block", boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                }}>
                    API ARCHITECTURE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Overview: External Data Exchange
                            </Typography>
                            <IconButton onClick={() => handleSpeak("APIs allow software applications to communicate and exchange data. In this ProDevConsult module, Hazzan demonstrates standardized data exchange and error handling.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            APIs empower your React components to go beyond static views. Whether it is a <b>RESTful API</b> using HTTP methods or a real-time <b>WebSocket</b>, these interactions form the backbone of modern cloud-based applications.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* ✅ Content Modules Rendering */}
                {apiModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}><AiOutlineRead /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                    <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                        {copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>

                            {/* Live Demo Interactions */}
                            <LiveDemoBox>
                                {i === 0 && (
                                    <Stack direction="row" spacing={2}>
                                        <Card sx={{ p: 2, bgcolor: "rgba(255,255,255,0.05)", color: "white", flex: 1, borderLeft: "4px solid crimson" }}>
                                            <Typography variant="subtitle1" fontWeight="bold">Hazzan</Typography>
                                            <Typography variant="caption">Architect</Typography>
                                        </Card>
                                        <Card sx={{ p: 2, bgcolor: "rgba(255,255,255,0.05)", color: "white", flex: 1, borderLeft: "4px solid crimson" }}>
                                            <Typography variant="subtitle1" fontWeight="bold">Alice</Typography>
                                            <Typography variant="caption">Designer</Typography>
                                        </Card>
                                    </Stack>
                                )}

                                {i === 1 && (
                                    <Grid container spacing={2}>
                                        {data.map(user => (
                                            <Grid item xs={12} sm={6} key={user.id}>
                                                <Card
                                                    sx={{
                                                        p: 2,
                                                        bgcolor: "rgba(255,255,255,0.05)",
                                                        cursor: "pointer",
                                                        borderLeft: "4px solid goldenrod",
                                                        "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
                                                    }}
                                                    onClick={() => setSelectedUser(user)}
                                                >
                                                    <Typography color="white" fontWeight="bold">{user.name}</Typography>
                                                    <Typography variant="caption" color="goldenrod">{user.email}</Typography>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}

                                {(i === 2) && (
                                    <Box sx={{ textAlign: "center" }}>
                                        <Typography variant="h6" color="goldenrod">
                                            {selectedUser ? `Selected: ${selectedUser.name} (${selectedUser.email})` : "Click a card above to inspect data"}
                                        </Typography>
                                    </Box>
                                )}
                            </LiveDemoBox>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">PROCEED TO NEXT INTEGRATION</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Utilities */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={() => window.speechSynthesis.cancel()}><StopIcon /></Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: "goldenrod" }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
